import React, { useCallback, useEffect, useRef, useState } from "react";
import { HardDrive, FileText, Image as ImageIcon, Video, Download, ExternalLink, Loader2, Check, Users, Share2, Search, Send, MessageSquare, Wand2, Clock, CloudUpload, Briefcase, MoreHorizontal, Trash2, Unlink, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";
import { fileUrl, downloadUrl, openPublish, mediaSrc, Lightbox } from "./MessageExtras";
import { thumbUrl, onThumbError } from "../lib/images";
import { Play } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { confirmAsync } from "./ConfirmDialog";

export const GALLERY_FILTERS = [
  { v: "all", label: "Semua" },
  { v: "document", label: "Dokumen" },
  { v: "image", label: "Gambar" },
  { v: "video", label: "Video" },
];

// Cursor-paginated gallery feed (newest first) with infinite scrolling.
export function useGallery(type = "all", limit = 24, q = "") {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const cursor = useRef(null);
  const busy = useRef(false);

  const fetchPage = useCallback(async (reset) => {
    if (busy.current) return;
    busy.current = true; setLoading(true);
    try {
      const before = reset ? "" : cursor.current ? `&before=${encodeURIComponent(cursor.current)}` : "";
      const r = await api.get(`/gallery?type=${type}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}${before}`);
      setItems((prev) => (reset ? r.data.items : [...prev, ...r.data.items]));
      setHasMore(!!r.data.has_more); cursor.current = r.data.next_before;
      if (r.data.retention_days) setRetentionDays(r.data.retention_days);
    } catch (e) { setHasMore(false); } finally { busy.current = false; setLoading(false); }
  }, [type, limit, q]);

  useEffect(() => { cursor.current = null; setItems([]); fetchPage(true); }, [fetchPage]);
  const loadMore = () => { if (hasMore && !busy.current) fetchPage(false); };
  const patch = (id, upd) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...upd } : it)));
  const remove = (id) => setItems((prev) => prev.filter((it) => it.id !== id));
  const reload = useCallback(() => { cursor.current = null; return fetchPage(true); }, [fetchPage]);
  return { items, loading, hasMore, loadMore, reload, patch, remove, retentionDays };
}

export const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

// "Simpan ke Drive" for platform-stored media (images / workspace videos) that will expire after the retention period.
export function DriveSaveButton({ item, driveConnected, onSaved }) {
  const [busy, setBusy] = useState(false);
  if (item.drive_id) return <span className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700" data-testid="gallery-drive-saved"><Check size={11} /> Tersimpan di Drive</span>;
  const save = async () => {
    if (driveConnected === false) { toast.error("Hubungkan Google Drive dulu di menu Integrasi"); window.location.href = "/integrations"; return; }
    setBusy(true);
    try { const r = await api.post("/gallery/drive-save", { id: item.id }); onSaved?.(item.id, { drive_id: r.data.drive_id, link: r.data.link }); toast.success("Tersimpan ke Google Drive (folder Oryntix)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan ke Drive"); }
    finally { setBusy(false); }
  };
  return <button onClick={save} disabled={busy} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="gallery-save-drive">{busy ? <Loader2 size={11} className="animate-spin" /> : <CloudUpload size={11} />} Simpan ke Drive</button>;
}

// One-click: back up every platform-stored image/video not yet on Drive. Runs as a background job we poll. Calls onDone to refresh the grid.
export function SaveAllToDriveButton({ driveConnected, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (driveConnected === false) { toast.error("Hubungkan Google Drive dulu di menu Integrasi"); window.location.href = "/integrations"; return; }
    setBusy(true);
    const t = toast.loading("Menyimpan semua media ke Google Drive…");
    try {
      await api.post("/gallery/drive-save-all");
      let done = false;
      for (let i = 0; i < 300 && !done; i++) {               // poll up to ~10 min
        await new Promise((r) => setTimeout(r, 2000));
        const s = (await api.get("/gallery/drive-save-all/status")).data || {};
        const { status, saved = 0, failed = 0, skipped = 0, error } = s;
        if (status === "running") { toast.loading(`Menyimpan ke Drive… ${saved} tersimpan`, { id: t }); continue; }
        done = true;
        if (status === "error") toast.error(error || "Gagal menyimpan ke Drive", { id: t });
        else if (!saved && !failed && !skipped) toast.success("Semua media sudah tersimpan di Drive", { id: t });
        else {
          const extra = [failed ? `${failed} gagal` : "", skipped ? `${skipped} dilewati (kedaluwarsa)` : ""].filter(Boolean).join(" · ");
          toast.success(`${saved} berkas tersimpan ke Google Drive (folder Oryntix)${extra ? ` · ${extra}` : ""}`, { id: t });
        }
        onDone?.();
      }
      if (!done) { toast.dismiss(t); onDone?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan ke Drive", { id: t }); }
    finally { setBusy(false); }
  };
  return <button onClick={run} disabled={busy} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60" data-testid="gallery-save-all-drive">{busy ? <Loader2 size={14} className="animate-spin" /> : <CloudUpload size={14} />} Simpan Semua ke Drive</button>;
}

export function RetentionBanner({ days = 30 }) {
  return (
    <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900" data-testid="gallery-retention-banner">
      <Clock size={14} className="mt-0.5 shrink-0" />
      <p><b>{`Retensi ${days} hari.`}</b> {`Gambar, video, dan berkas yang Anda unggah ke Galeri dihapus otomatis ${days} hari setelah dibuat. Kami mengirim`} <b>pengingat H-3</b> sebelum berkas dihapus. Pilih <b>Simpan ke Drive</b> dari menu ⋯ pada kartu atau <b>Simpan Semua ke Drive</b> untuk menyimpannya permanen di Google Drive Anda (folder Oryntix) — tautan Drive tetap tampil di sini setelah file platform kedaluwarsa. Dokumen hasil Ruang Kerja tidak terpengaruh.</p>
    </div>
  );
}

// Fires onVisible whenever the sentinel scrolls into view.
export function Sentinel({ onVisible, root }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) onVisible(); }, { root: root?.current || null, rootMargin: "200px" });
    io.observe(el); return () => io.disconnect();
  }, [onVisible, root]);
  return <div ref={ref} className="h-1 w-full" data-testid="gallery-sentinel" />;
}

const exportUrl = (taskId, fmt) => mediaUrl(`/tasks/${taskId}/export/${fmt}`);
const KIND_ICON = { document: FileText, image: ImageIcon, video: Video, drive: HardDrive };

// Build the per-card menu entries (links + actions) for every gallery item kind.
function cardActions({ item, onShare, driveConnected, onPatch, onRemove, setZoom }) {
  const a = [];
  const link = (label, href, Icon, testid, extra = {}) => a.push({ label, href, Icon, testid, ...extra });
  const act = (label, run, Icon, testid, extra = {}) => a.push({ label, run, Icon, testid, ...extra });
  if (item.kind === "drive") link("Buka di Google Drive", item.link, ExternalLink, "gallery-open-drive", { blank: true });
  else if (item.kind === "document" && item.source !== "upload") {
    link("Buka di Ruang Kerja", `/workspace/${item.task_id}`, Briefcase, "gallery-open");
    link("Unduh Word", exportUrl(item.task_id, "docx"), Download, "gallery-dl-docx", { download: true });
    link("Unduh PDF", exportUrl(item.task_id, "pdf"), Download, "gallery-dl-pdf", { download: true });
  } else if (item.path) {
    if (item.kind === "image" && setZoom) act("Lihat", () => setZoom(true), ImageIcon, "gallery-open");
    else link("Buka", fileUrl(item.path), ExternalLink, "gallery-open", { blank: true });
    link("Unduh", downloadUrl(item.path), Download, "gallery-dl-file", { download: item.name });
    if (item.drive_id && item.link) link("Buka di Google Drive", item.link, HardDrive, "gallery-open-drive", { blank: true });
    if (item.conversation_id) link("Buka Chat", `/chat/${item.conversation_id}`, MessageSquare, "gallery-open-chat");
    if (item.source === "workspace") link("Ruang Kerja", `/workspace/${item.task_id}`, Briefcase, "gallery-open-workspace");
    if (item.kind !== "document") act("Publikasikan", () => openPublish({ type: item.kind, path: item.path, name: item.name }, item.title || ""), Send, "gallery-publish");
  } else {
    if (item.link) link("Buka di Google Drive", item.link, HardDrive, "gallery-open-drive", { blank: true });
    if (item.conversation_id) link("Buka Chat", `/chat/${item.conversation_id}`, MessageSquare, "gallery-open-chat");
    if (item.source === "workspace") link("Ruang Kerja", `/workspace/${item.task_id}`, Briefcase, "gallery-open-workspace-drive");
    if (item.drive_id && item.kind !== "document") act("Publikasikan", () => openPublish({ type: item.kind, drive_id: item.drive_id, name: item.name }, item.title || ""), Send, "gallery-publish");
  }
  if (onShare && item.kind !== "drive") act("Bagikan", () => onShare(item), Share2, "gallery-share");
  if (item.path && item.expires_at && !item.drive_id) act("Simpan ke Drive", async () => {
    if (driveConnected === false) { toast.error("Hubungkan Google Drive dulu di menu Integrasi"); window.location.href = "/integrations"; return; }
    const r = await api.post("/gallery/drive-save", { id: item.id }); onPatch?.(item.id, { drive_id: r.data.drive_id, link: r.data.link }); toast.success("Tersimpan ke Google Drive (folder Oryntix)");
  }, CloudUpload, "gallery-save-drive", { tone: "emerald" });
  if (item.drive_id || item.kind === "drive") act("Lepas tautan Drive", async () => {
    await api.post("/gallery/unlink-drive", { id: item.id });
    if (item.kind === "drive" || !item.path) onRemove?.(item.id); else onPatch?.(item.id, { drive_id: null, link: null });
    toast.success("Tautan Drive dilepas dari Galeri — file di Google Drive tetap ada");
  }, Unlink, "gallery-unlink-drive", { divider: true });
  act(item.kind === "document" && item.source !== "upload" ? "Hapus dokumen" : item.kind === "drive" ? "Hapus dari Galeri" : "Hapus file", async () => {
    const what = item.kind === "document" && item.source !== "upload" ? "Hapus dokumen ini beserta hasil tugasnya di Ruang Kerja?" : item.kind === "drive" ? "Hapus item ini dari Galeri? File di Google Drive tetap ada." : "Hapus file ini dari server platform? Tindakan ini tidak bisa dibatalkan (salinan di Drive, bila ada, tetap aman).";
    if (!await confirmAsync(what)) return;
    await api.post("/gallery/delete", { id: item.id }); onRemove?.(item.id); toast.success("Berkas dihapus");
  }, Trash2, "gallery-delete", { tone: "rose", divider: !(item.drive_id || item.kind === "drive") });
  return a;
}

// Horizontal three-dot menu in the card corner: every action for the item lives here.
export function CardMenu({ actions }) {
  const [busy, setBusy] = useState("");
  const run = async (x) => { setBusy(x.testid); try { await x.run(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); } finally { setBusy(""); } };
  const cls = (x) => `flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-semibold ${x.tone === "rose" ? "text-rose-600 focus:bg-rose-50 focus:text-rose-700" : x.tone === "emerald" ? "text-emerald-700 focus:bg-emerald-50" : "text-slate-700 focus:bg-[#EEF3FF] focus:text-[#2F6BFF]"}`;
  return (
    <div className="absolute right-2 top-2 z-10" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button aria-label="Menu berkas" data-testid="gallery-card-menu" className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#2F6BFF] to-[#7C3AED] text-white shadow-lg shadow-[#2F6BFF]/40 ring-2 ring-white transition hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-[#2F6BFF]/60 active:scale-95"><MoreHorizontal size={17} /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6} className="z-[95] w-52 rounded-xl border-[#E7ECF3] bg-white p-1.5 text-slate-700 shadow-xl" data-testid="gallery-card-menu-content">
          {actions.map((x, i) => (
            <React.Fragment key={x.testid + i}>
              {x.divider && <DropdownMenuSeparator className="my-1" />}
              {x.href ? (
                <DropdownMenuItem asChild className={cls(x)}>
                  <a href={x.href} target={x.blank ? "_blank" : undefined} rel={x.blank ? "noreferrer" : undefined} download={x.download === true ? "" : x.download || undefined} data-testid={x.testid}><x.Icon size={14} /> {x.label}</a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem className={cls(x)} disabled={busy === x.testid} onSelect={() => run(x)} data-testid={x.testid}>{busy === x.testid ? <Loader2 size={14} className="animate-spin" /> : <x.Icon size={14} />} {x.label}</DropdownMenuItem>
              )}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// "Bebaskan ruang": purge every platform-retained media now (what the 30-day retention would do later); Drive copies stay.
export function FreeSpaceButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gallery/free-space");
      const n = (r.data.messages || 0) + (r.data.videos || 0) + (r.data.uploads || 0);
      toast.success(n ? `${n} media dihapus dari penyimpanan platform. Salinan di Google Drive tetap ada.` : "Tidak ada media yang bisa dibebaskan.");
      onDone?.(r.data.storage);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membebaskan ruang"); }
    setBusy(false); setConfirm(false);
  };
  if (confirm) {
    return (
      <span className="flex items-center gap-1.5 text-[11px]" data-testid="free-space-confirm">
        <span className="text-slate-600">Hapus SEMUA gambar/video/unggahan di penyimpanan platform? Yang sudah disimpan ke Drive tetap aman.</span>
        <button onClick={run} disabled={busy} className="rounded-lg bg-[#EF4444] px-2.5 py-1 font-bold text-white disabled:opacity-60" data-testid="free-space-yes">{busy ? <Loader2 size={12} className="animate-spin" /> : "Ya, bebaskan"}</button>
        <button onClick={() => setConfirm(false)} disabled={busy} className="rounded-lg border border-[#E7ECF3] px-2.5 py-1 font-semibold text-slate-600" data-testid="free-space-no">Batal</button>
      </span>
    );
  }
  return <button onClick={() => setConfirm(true)} className="shrink-0 rounded-lg border border-[#E7ECF3] px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-[#EF4444] hover:text-[#EF4444]" data-testid="free-space-btn">Bebaskan ruang</button>;
}


// Upload files straight into the Gallery (same limits as chat attachments — set by the platform admin; retention applies).
export function GalleryUploadButton({ onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [limits, setLimits] = useState(null);
  useEffect(() => { api.get("/upload-limits").then((r) => setLimits(r.data)).catch(() => {}); }, []);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files.slice(0, 10)) {
      const isVideo = /\.(mp4|webm|mov|m4v)$/i.test(f.name) || f.type.startsWith("video/");
      const maxMb = limits ? (isVideo ? limits.video_mb : limits.attachment_mb) : null;
      if (maxMb && f.size > maxMb * 1024 * 1024) { toast.error(`${f.name}: ${isVideo ? "video" : "berkas"} maksimal ${maxMb} MB (batas admin platform)`); continue; }
      const t = toast.loading(`Mengunggah ${f.name}…`);
      try {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (p) => { if (p.total) toast.loading(`Mengunggah ${f.name}… ${Math.round((p.loaded / p.total) * 100)}%`, { id: t }); } });
        toast.success(`${f.name} tersimpan di Galeri`, { id: t }); ok += 1; onUploaded?.(r.data);
      } catch (err) { toast.error(err?.response?.data?.detail || `${f.name}: gagal diunggah`, { id: t }); }
    }
    setBusy(false);
    if (ok) window.dispatchEvent(new Event("oryntix:gallery-changed"));
  };
  return (
    <>
      <input ref={ref} type="file" multiple className="hidden" accept="image/*,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v,.pdf,.docx,.xlsx,.pptx,.txt,.md,.csv" onChange={pick} data-testid="gallery-upload-input" />
      <button onClick={() => ref.current?.click()} disabled={busy} title={limits ? `Maks ${limits.attachment_mb} MB per berkas, video ${limits.video_mb} MB` : ""} className="btn-grad flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs disabled:opacity-60" data-testid="gallery-upload-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Unggah Berkas{limits ? <span className="font-normal opacity-80"> · maks {limits.attachment_mb}/{limits.video_mb} MB</span> : null}</button>
    </>
  );
}

function VideoTile({ item }) {
  const [poster, setPoster] = useState(!item.url && !item.drive_id);  // stored video → first-frame poster; external/Drive → the video element as before
  return (
    <div className="relative h-full w-full bg-black" data-testid="gallery-video-tile">
      {poster ? <img src={thumbUrl(item.path, 320)} alt={item.name} loading="lazy" decoding="async" onError={() => setPoster(false)} className="h-full w-full object-cover" data-testid="gallery-video-poster" />
        : <video src={mediaSrc(item)} preload="metadata" muted className="h-full w-full object-cover" />}
      <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white"><Play size={16} fill="currentColor" /></span></span>
    </div>
  );
}

export function GalleryCard({ item, selectable = false, selected = false, onSelect, onOpen, onShare, driveConnected, onPatch, onRemove }) {
  const Icon = KIND_ICON[item.kind] || FileText;
  const date = new Date(item.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  const left = item.expires_at && item.path ? daysLeft(item.expires_at) : null;
  const [zoom, setZoom] = useState(false);
  const click = selectable ? () => onSelect(item) : onOpen ? () => onOpen(item) : item.kind === "image" ? () => setZoom(true) : undefined;
  return (
    <div onClick={click} data-testid={`gallery-item-${item.kind}`} className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white transition ${selectable || onOpen || item.kind === "image" ? "cursor-pointer" : ""} ${selected ? "border-[#2F6BFF] ring-2 ring-[#2F6BFF]/30" : "border-[#E7ECF3] hover:shadow-md"}`}>
      {zoom && <Lightbox media={{ type: "image", path: item.path, name: item.name, request: item.caption || item.title }} onClose={() => setZoom(false)} />}
      {selectable && <span className={`absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border bg-white ${selected ? "btn-grad border-transparent" : "border-slate-300"}`} data-testid="gallery-select-mark">{selected && <Check size={13} />}</span>}
      {!selectable && <CardMenu actions={cardActions({ item, onShare, driveConnected, onPatch, onRemove, setZoom })} />}
      <div className="flex h-36 items-center justify-center overflow-hidden bg-slate-50">
        {item.kind === "image" && item.path ? <img src={item.url || item.drive_id ? mediaSrc(item) : thumbUrl(item.path, 320)} onError={onThumbError(mediaSrc(item))} alt={item.name} loading="lazy" decoding="async" className="h-full w-full object-cover" data-testid="gallery-thumb" />
          : item.kind === "video" && item.path ? <VideoTile item={item} />
          : <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EEF3FF] text-[#2F6BFF]"><Icon size={26} /></span>}
      </div>
      <div className="flex flex-1 flex-col p-3">
        <p className="truncate text-sm font-semibold text-slate-900" title={item.title || item.name} data-testid="gallery-item-title">{item.edited && <Wand2 size={11} className="mr-1 inline text-[#2F6BFF]" />}{item.title || item.name}</p>
        {item.caption && <p className="mt-0.5 line-clamp-2 text-xs text-[#1D4ED8]" title={item.caption} data-testid="gallery-item-caption">“{item.caption}”</p>}
        <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-400">{item.team && <Users size={11} />}{item.source === "upload" && <Upload size={11} />}{item.persona_name || "Asisten"} · {date}{item.version > 1 ? ` · v${item.version}` : ""}</p>
        {left !== null && !item.drive_id && <p className={`mt-0.5 flex items-center gap-1 text-[11px] ${left <= 5 ? "font-semibold text-rose-600" : "text-amber-700"}`} data-testid="gallery-item-expiry"><Clock size={11} /> {left <= 0 ? "Kedaluwarsa hari ini" : `Dihapus otomatis dalam ${left} hari`}</p>}
        {item.drive_id && <p className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid="gallery-drive-saved"><Check size={11} /> Tersimpan di Drive</p>}
      </div>
    </div>
  );
}

export function GalleryGrid({ type, q = "", selectable = false, selectedIds = [], onSelect, onOpen, onShare, scrollRoot, driveConnected, emptyText = "Belum ada berkas. Minta asisten membuat dokumen, gambar, atau video dari chat, atau unggah berkas Anda sendiri." }) {
  const g = useGallery(type, 24, q);
  useEffect(() => { const h = () => g.reload(); window.addEventListener("oryntix:gallery-changed", h); return () => window.removeEventListener("oryntix:gallery-changed", h); }, [g.reload]); // eslint-disable-line
  return (
    <div data-testid="gallery-grid">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {g.items.map((it) => <GalleryCard key={it.id} item={it} selectable={selectable} selected={selectedIds.includes(it.id)} onSelect={onSelect} onOpen={onOpen} onShare={onShare} driveConnected={driveConnected} onPatch={g.patch} onRemove={g.remove} />)}
      </div>
      {g.loading && <p className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400" data-testid="gallery-loading"><Loader2 size={16} className="animate-spin" /> Memuat berkas…</p>}
      {!g.loading && g.items.length === 0 && <p className="py-12 text-center text-sm text-slate-400" data-testid="gallery-empty">{q ? `Tidak ada berkas yang cocok dengan «${q}».` : emptyText}</p>}
      {!g.loading && g.hasMore && <Sentinel onVisible={g.loadMore} root={scrollRoot} />}
      {!g.loading && !g.hasMore && g.items.length > 0 && <p className="py-4 text-center text-[11px] text-slate-300">Semua berkas sudah ditampilkan</p>}
    </div>
  );
}

export function GallerySearch({ value, onChange }) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => onChange(v.trim()), 350); return () => clearTimeout(t); }, [v]); // eslint-disable-line
  return (
    <div className="relative w-full sm:w-72"><Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className="input-dark py-2 pl-10" placeholder="Cari prompt gambar/video, judul, atau isi dokumen…" value={v} onChange={(e) => setV(e.target.value)} data-testid="gallery-search" /></div>
  );
}

export function GalleryFilterBar({ value, onChange, counts }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
      {GALLERY_FILTERS.map((f) => (
        <button key={f.v} onClick={() => onChange(f.v)} data-testid={`gallery-filter-${f.v}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${value === f.v ? "btn-grad" : "border border-[#E7ECF3] bg-white text-slate-600 hover:bg-slate-50"}`}>{f.label}</button>
      ))}
    </div>
  );
}
